import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import StatisticsCard from "./components/StatisticsCard.tsx";
import UsersChart from "./components/UsersChart.tsx";
import SubscriptionsChart from "./components/SubscriptionsChart.tsx";
import RevenueChart from "./components/RevenueChart.tsx";
import MarketLeaders from "./components/MarketLeaders.tsx";
import "./Dashboard.scss";
import { dashboardApi } from "./DashboardApi";
import { useApiLoader } from "../../hooks/useApiLoader";

// Icons using react-bootstrap-icons
import {
  BoxSeam,
  Calendar2Event,
  ClockHistory,
  Truck,
  CheckCircle,
} from "react-bootstrap-icons";

const Dashboard = () => {
  const { withLoader } = useApiLoader();

  // State for dashboard data
  const [giftCounts, setGiftCounts] = useState({
    totalOrders: 0,
    upcoming: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
  });

  const [usersData, setUsersData] = useState([
    { name: "Active", value: 0, color: "#3957A0" },
    { name: "Inactive", value: 0, color: "#D7A360" },
  ]);

  const [subscriptionsData, setSubscriptionsData] = useState<
    { month: string; value: number }[]
  >([]);

  const [revenueData, setRevenueData] = useState<
    { month: string; value: number }[]
  >([]);

  const [marketLeadersData, setMarketLeadersData] = useState<
    { id: number; location: string; count: number }[]
  >([]);

  // Fetch all dashboard data
  const fetchDashboardData = async () => {
    await withLoader(async () => {
      // Fetch all data in parallel
      const [
        giftCountsResponse,
        userStatusResponse,
        revenueResponse,
        topStatesResponse,
        subscriptionsResponse,
      ] = await Promise.all([
        dashboardApi.getPaidParentGiftCounts(),
        dashboardApi.getUserStatusCounts(),
        dashboardApi.getTotalRevenue(),
        dashboardApi.getTopStatesByUsers(),
        dashboardApi.getSubscriptionCounts15Days(),
      ]);

      // Log all responses for debugging
      console.log('Dashboard API Responses:', {
        giftCountsResponse,
        userStatusResponse,
        revenueResponse,
        topStatesResponse,
        subscriptionsResponse,
      });

      // Update gift counts
      if (giftCountsResponse.status === "Success" && giftCountsResponse.data) {
        const breakdown = giftCountsResponse.data.statusBreakdown;
        setGiftCounts({
          totalOrders: giftCountsResponse.data.totalPaidParentGifts,
          upcoming: breakdown.upcoming,
          processing: breakdown.processing,
          shipped: breakdown.shipped,
          delivered: breakdown.delivered,
        });
      }

      // Update user status counts
      if (
        userStatusResponse.status === "Success" &&
        userStatusResponse.data &&
        userStatusResponse.data.statusBreakdown
      ) {
        setUsersData([
          {
            name: "Active",
            value: userStatusResponse.data.statusBreakdown.active,
            color: "#3957A0",
          },
          {
            name: "Inactive",
            value: userStatusResponse.data.statusBreakdown.inactive,
            color: "#D7A360",
          },
        ]);
      }

      // Update revenue data
      if (revenueResponse.status === "Success" && revenueResponse.data) {
        console.log('revenueResponse.data:', revenueResponse.data);
        console.log('monthlyRevenue:', revenueResponse.data.monthlyRevenue);
        // Access monthlyRevenue array from the nested data
        if (revenueResponse.data.monthlyRevenue && Array.isArray(revenueResponse.data.monthlyRevenue)) {
          // Create a full 12-month array (Jan - Dec)
          const allMonths = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
          ];

          // Create a map of month names to revenue
          const revenueMap = new Map(
            revenueResponse.data.monthlyRevenue.map(item => [
              item.monthName,
              item.totalRevenue
            ])
          );

          // Build the full 12-month revenue data
          const formattedRevenue = allMonths.map(monthName => ({
            month: monthName.substring(0, 3), // Convert to "Jan", "Feb", etc.
            value: revenueMap.get(monthName) || 0
          }));

          console.log('formattedRevenue:', formattedRevenue);
          setRevenueData(formattedRevenue);
        } else {
          console.warn('monthlyRevenue is missing or not an array');
        }
      }

      // Update market leaders data
      if (topStatesResponse.status === "Success" && topStatesResponse.data) {
        console.log('topStatesResponse.data:', topStatesResponse.data);
        console.log('topStates:', topStatesResponse.data.topStates);
        // Access topStates array from the nested data
        if (topStatesResponse.data.topStates && Array.isArray(topStatesResponse.data.topStates)) {
          const formattedData = topStatesResponse.data.topStates.map((state, index) => ({
            id: index + 1,
            location: state.stateName,
            count: state.count,
          }));
          console.log('formattedMarketLeaders:', formattedData);
          setMarketLeadersData(formattedData);
        } else {
          console.warn('topStates is missing or not an array');
        }
      }

      // Update subscriptions data
      if (
        subscriptionsResponse.status === "Success" &&
        subscriptionsResponse.data
      ) {
        console.log('subscriptionsResponse.data:', subscriptionsResponse.data);
        console.log('dailyBreakdown:', subscriptionsResponse.data.dailyBreakdown);
        // Access dailyBreakdown array from the nested data
        if (subscriptionsResponse.data.dailyBreakdown && Array.isArray(subscriptionsResponse.data.dailyBreakdown)) {
          // Transform the subscription data - take only first 15 days
          const formattedSubscriptions = subscriptionsResponse.data.dailyBreakdown
            .slice(0, 15)
            .map((item) => {
              const date = new Date(item.date);
              const month = date.toLocaleDateString("en-US", { month: "short" });
              const day = date.getDate();
              return {
                month: `${month} ${day}`,
                value: item.count,
              };
            });
          console.log('formattedSubscriptions:', formattedSubscriptions);
          setSubscriptionsData(formattedSubscriptions);
        } else {
          console.warn('dailyBreakdown is missing or not an array');
        }
      }
    }, "Loading dashboard data...");
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Statistics cards data
  const statisticsData = [
    {
      icon: <BoxSeam size={24} color="#3957A0" />,
      count: giftCounts.totalOrders,
      label: "Total Orders",
      iconBgColor: "#EFF5FF",
    },
    {
      icon: <Calendar2Event size={24} color="#3957A0" />,
      count: giftCounts.upcoming,
      label: "Upcoming",
      iconBgColor: "#EFF5FF",
    },
    {
      icon: <ClockHistory size={24} color="#3957A0" />,
      count: giftCounts.processing,
      label: "Processing",
      iconBgColor: "#EFF5FF",
    },
    {
      icon: <Truck size={24} color="#3957A0" />,
      count: giftCounts.shipped,
      label: "Shipped",
      iconBgColor: "#EFF5FF",
    },
    {
      icon: <CheckCircle size={24} color="#3957A0" />,
      count: giftCounts.delivered,
      label: "Delivered",
      iconBgColor: "#EFF5FF",
    },
  ];

  // Log state before rendering
  console.log('Render - usersData:', usersData);
  console.log('Render - subscriptionsData:', subscriptionsData);
  console.log('Render - revenueData:', revenueData);
  console.log('Render - marketLeadersData:', marketLeadersData);

  return (
    <section className="main-body">
      <Container fluid>
        {/* Statistics Cards - Row 1 */}
        <Row className="statistics-row g-3">
          {statisticsData.map((stat, index) => (
            <Col key={index} xs={6} sm={6} md={4} lg className="stat-col">
              <StatisticsCard
                icon={stat.icon}
                count={stat.count}
                label={stat.label}
                iconBgColor={stat.iconBgColor}
              />
            </Col>
          ))}
        </Row>

        {/* Charts Section - Row 2: Users and Subscriptions */}
        <Row className="charts-row g-3">
          {/* Users Chart */}
          <Col xs={12} md={6} className="mb-3">
            <UsersChart data={usersData} />
          </Col>

          {/* Subscriptions Chart */}
          <Col xs={12} md={6} className="mb-3">
            <SubscriptionsChart data={subscriptionsData} />
          </Col>
        </Row>

        {/* Row 3: Revenue Chart and Market Leaders */}
        <Row className="g-3">
          {/* Revenue Chart - 8 columns */}
          <Col xs={12} md={8} className="mb-3">
            <RevenueChart data={revenueData} />
          </Col>

          {/* Market Leaders - 4 columns */}
          <Col xs={12} md={4} className="mb-3">
            <MarketLeaders data={marketLeadersData} />
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Dashboard;
